/**
 * Hydrate the CRM's provider settings from `core.setting` (#524).
 *
 * The two CRM stores (`geo`, `companyLookup`) expose SYNCHRONOUS accessors —
 * `getCompanySettings()` is called from phone normalisation, which has no place
 * to await — so they stay module-level caches. This is the one place that fills
 * them, once, at startup, exactly as `BrandSync` fills the brand.
 *
 * Before it resolves, the DEFAULTS apply: the offline built-in providers and no
 * keys. That is the honest pre-hydration state rather than a spinner over a
 * settings read nobody is waiting for — a map that has not appeared yet is not
 * a failure, and an address lookup silently falling back to the offline book is
 * better than one that errors while the settings are in flight.
 *
 * Writes go through `client.setSetting`, which is admin-gated server-side and
 * audited. That is the entire point of the move: the previous path let any
 * signed-in user set an API key for themselves, in plaintext, with no record.
 *
 * Nothing this file fills is a credential. Every CRM secret is read server-side
 * — see `SECRET_MASK` below for the defect that made that rule explicit.
 */
import { useEffect } from 'react'
import type { SolumClient } from '@solum/sdk'
import { applyGeoSettings, type GeoSettings } from '../record/geo'
import { applyCompanySettings, type CompanySettings } from '../record/companyLookup'

/** `crm.<key>` → the field it fills, per store. NO key ever appears here: every
 *  CRM credential is read server-side, and the only thing a client could get
 *  from `describe_settings` is the mask. */
const GEO_KEYS: Record<string, keyof GeoSettings> = {
  map_provider: 'mapProvider', lookup_provider: 'lookupProvider',
}
const COMPANY_KEYS: Record<string, keyof CompanySettings> = {
  vat_provider: 'vatProvider', vat_requester: 'vatRequester', phone_country: 'phoneCountry',
}
/** `crm.registry_<x>` → the flag it sets. Four booleans, one per registry. */
const REGISTRY_KEYS: Record<string, keyof CompanySettings['registries']> = {
  registry_builtin: 'builtin', registry_brreg: 'brreg', registry_fr: 'fr', registry_uk: 'uk',
}

/**
 * The mask `core.describe_settings` (core 0034) substitutes for a SET secret.
 * It is a value, not an absence — the original guard here skipped `''` on the
 * belief that "an empty string means set but not shown to you", and the real
 * contract is the opposite way round: unset answers null, and SET answers this.
 * So the mask landed in the cache, and `mapEmbedUrl` interpolated it into a
 * Google embed URL — the first admin to fill the key in broke the map (#524).
 * Nothing masked may reach a store, so the mask is rejected outright.
 */
const SECRET_MASK = '••••••'

function assign<T extends object>(target: Partial<T>, field: keyof T, value: unknown) {
  if (value === null || value === undefined) return
  if (typeof value === 'string' && (value === '' || value === SECRET_MASK)) return
  target[field] = value as T[keyof T]
}

export async function hydrateCrmSettings(client: SolumClient): Promise<void> {
  const rows = await client.describeSettings('crm')
  const geo: Partial<GeoSettings> = {}
  const company: Partial<CompanySettings> = {}
  const registries: Partial<CompanySettings['registries']> = {}
  for (const r of rows) {
    // A secret has no destination in the browser at all; skipping it before the
    // mapping means a future declaration cannot quietly acquire one.
    if (r.is_secret) continue
    const v = (r as { value?: unknown; default_value?: unknown }).value
      ?? (r as { default_value?: unknown }).default_value
    if (GEO_KEYS[r.key]) assign(geo, GEO_KEYS[r.key]!, v)
    if (COMPANY_KEYS[r.key]) assign(company, COMPANY_KEYS[r.key]!, v)
    if (REGISTRY_KEYS[r.key] && typeof v === 'boolean') registries[REGISTRY_KEYS[r.key]!] = v
  }
  if (Object.keys(registries).length) company.registries = registries as CompanySettings['registries']
  if (Object.keys(geo).length) applyGeoSettings(geo)
  if (Object.keys(company).length) applyCompanySettings(company)
}

/** Mount once in the shell. Silent on failure: a settings read that fails must
 *  not take the console down — the defaults are usable, which is why they exist. */
export function CrmSettingsSync({ client, isLive }: { client: SolumClient; isLive: boolean }) {
  useEffect(() => {
    if (!isLive) return
    void hydrateCrmSettings(client).catch(() => { /* defaults stand */ })
  }, [client, isLive])
  return null
}
